import { useToast } from '@/components/ui/toast'
import type { Document, ReorganizationOperationRequest } from '@/types/generated'
import { DocumentsService } from '@/types/generated'
import type { DocumentGroup } from './useDocumentState'
import { usePdfOperations } from './usePdfOperations'

export const useApiOperations = (
  t: (key: string, params?: Record<string, any>) => string,
  documentState: any
) => {
  const { toast } = useToast()
  const { isSaving } = documentState
  const { generatePDFFromDocumentGroup } = usePdfOperations(t)

  /**
   * Save document reorganization changes to the backend
   */
  const saveDocumentReorganization = async (
    caseUuid: string,
    organizedDocuments: DocumentGroup[],
    originalDocumentState: DocumentGroup[],
    documents: Document[],
    hasChanges: boolean,
    pageRotations?: Map<string, number> | Record<string, number>,
    pendingNameChanges?: Map<string, string>,
    onSuccess?: () => void,
    onError?: (error: any) => void
  ): Promise<boolean> => {
    if (!hasChanges) {
      toast({
        title: t('info'),
        description: t('noChangesToSave'),
        variant: 'default'
      })
      return false
    }

    try {
      isSaving.value = true

      // Show loading state
      toast({
        title: t('info'),
        description: t('savingChanges'),
        variant: 'default'
      })

      // Process pending renames BEFORE reorganize so the backend's replaced_title snapshot
      // captures the user's edited name rather than the stale title.
      let hadNameChanges = false
      let nameChangesProcessedSuccessfully = false

      if (pendingNameChanges && pendingNameChanges.size > 0) {
        hadNameChanges = true
        const nameChangeResults = await processPendingNameChanges(pendingNameChanges)

        nameChangeResults.successful.forEach((uuid) => {
          pendingNameChanges.delete(uuid)
        })

        nameChangesProcessedSuccessfully = nameChangeResults.successful.length > 0

        if (nameChangeResults.failed.length > 0) {
          console.warn(
            `${nameChangeResults.failed.length} name changes failed, continuing with reorganization`
          )
        }
      }

      const operations = await generateReorganizationOperations(
        organizedDocuments,
        originalDocumentState,
        documents,
        pageRotations
      )

      // If we have no operations but we successfully processed name changes, consider it a success
      if (operations.length === 0) {
        if (hadNameChanges && nameChangesProcessedSuccessfully) {
          // Only name changes were made and they were processed successfully
          toast({
            title: t('success'),
            description: t('changesSavedSuccessfully'),
            variant: 'default'
          })

          // Call success callback for name-only changes
          if (onSuccess) {
            onSuccess()
          }

          return true
        } else {
          // No changes at all
          toast({
            title: t('info'),
            description: t('noChangesToSave'),
            variant: 'default'
          })
          return false
        }
      }

      // Call the reorganization API
      const response = await DocumentsService.documentsReorganizeCreate({
        requestBody: {
          case_uuid: caseUuid,
          operations: operations
        }
      })

      toast({
        title: t('success'),
        description: t('changesSavedSuccessfully'),
        variant: 'default'
      })

      // Call success callback if provided
      if (onSuccess) {
        onSuccess()
      }

      return true
    } catch (error: any) {
      console.error('Error saving documents:', error)

      // Provide more specific error messages
      let errorMessage = t('failedToSaveChanges')
      if (error.body?.error) {
        errorMessage = error.body.error
      } else if (error.body?.details) {
        errorMessage = error.body.details
      } else if (error.message) {
        errorMessage = error.message
      }

      toast({
        title: t('error'),
        description: errorMessage,
        variant: 'destructive'
      })

      // Call error callback if provided
      if (onError) {
        onError(error)
      }

      return false
    } finally {
      isSaving.value = false
    }
  }

  /**
   * Generate reorganization operations for saving documents
   */
  const generateReorganizationOperations = async (
    organizedDocuments: DocumentGroup[],
    originalDocumentState: DocumentGroup[],
    documents: Document[],
    pageRotations?: Map<string, number> | Record<string, number>
  ): Promise<ReorganizationOperationRequest[]> => {
    const operations: ReorganizationOperationRequest[] = []

    // Get a summary of what changes were made
    const changeSummary = getChangeSummary(organizedDocuments, originalDocumentState)

    if (changeSummary.type === 'no_changes') {
      return operations
    }

    // Get the current change tracker - FIX: Use originalDocumentState instead of documents
    const tracker = trackDocumentChanges(organizedDocuments, originalDocumentState, documents)

    // Only include documents that have actually changed
    let changedDocumentUuids = Array.from(tracker.values())
      .filter((change) => change.hasChanges && change.changeType !== 'created')
      .map((change) => change.originalUuid)

    // Also include documents that were added after initial load (not in originalDocumentState)
    // These are documents added via AddDocumentsDialog
    const newlyAddedDocumentUuids = new Set<string>()
    organizedDocuments.forEach((docGroup) => {
      docGroup.pages.forEach((page) => {
        // Check if this page's document wasn't in any original document group
        const wasInOriginal = originalDocumentState.some((originalDoc) =>
          originalDoc.pages.some((originalPage) => originalPage.documentUuid === page.documentUuid)
        )
        if (!wasInOriginal && documents.some((d) => d.uuid === page.documentUuid)) {
          newlyAddedDocumentUuids.add(page.documentUuid)
        }
      })
    })
    changedDocumentUuids = [...changedDocumentUuids, ...Array.from(newlyAddedDocumentUuids)]

    // If we have page rotations but no structural changes detected,
    // we still need to include documents that have rotated pages
    if (changedDocumentUuids.length === 0 && pageRotations) {
      const rotationMap =
        pageRotations instanceof Map ? pageRotations : new Map(Object.entries(pageRotations || {}))
      if (rotationMap.size > 0) {
        // Find documents that have pages with rotations
        const documentsWithRotations = new Set<string>()
        for (const [pageUuid, rotation] of rotationMap.entries()) {
          if (rotation !== 0) {
            // Find which document this page belongs to
            for (const docGroup of organizedDocuments) {
              const page = docGroup.pages.find((p) => p.uuid === pageUuid)
              if (page) {
                documentsWithRotations.add(page.documentUuid)
                break
              }
            }
          }
        }
        changedDocumentUuids = Array.from(documentsWithRotations)
      }
    }

    // Create the appropriate operation based on the change type
    const operation: ReorganizationOperationRequest = {
      operation_type: (() => {
        if (changeSummary.type === 'merge') return 'merge'
        if (changeSummary.type === 'split') return 'split'
        if (changeSummary.type === 'reorganization') return 'reorganize'
        return 'reorganize'
      })(),
      // Only send UUIDs of documents that have actually changed
      old_document_uuids: changedDocumentUuids,
      new_document_data: {
        documents: (await Promise.all(
          // Only process document groups that correspond to changed documents
          organizedDocuments
            .filter((docGroup) => {
              // For merge operations, we need to include ALL new document groups that contain
              // pages from the documents that were involved in the merge (changed documents)
              const hasChangedPages = docGroup.pages.some((page) =>
                changedDocumentUuids.includes(page.documentUuid)
              )
              // Also include newly created document groups (in case pages were moved to a new document)
              const isNewlyCreated = docGroup.pages.every(
                (page) => !documents.some((d) => d.uuid === page.documentUuid)
              )
              // Also include document groups that are new (not in originalDocumentState)
              // This handles documents added via the AddDocumentsDialog after initial load
              const isNewDocumentGroup = !originalDocumentState.some((originalDoc) => {
                // Check if this doc group has the same pages as any original doc group
                const originalPageUuids = originalDoc.pages
                  .map((p) => p.uuid)
                  .sort()
                  .join(',')
                const currentPageUuids = docGroup.pages
                  .map((p) => p.uuid)
                  .sort()
                  .join(',')
                return originalPageUuids === currentPageUuids
              })
              return hasChangedPages || isNewlyCreated || isNewDocumentGroup
            })
            // Remove duplicates based on content
            .filter((docGroup, index, array) => {
              const firstIndex = array.findIndex((dg) => dg.name === docGroup.name)
              return firstIndex === index
            })
            .map(async (docGroup) => {
              try {
                return {
                  title: docGroup.name,
                  filename: `${docGroup.name}.pdf`,
                  pdf_content: await generatePDFFromDocumentGroup(docGroup, pageRotations)
                }
              } catch (error) {
                console.error(`Failed to generate PDF for document group: ${docGroup.name}`, error)
                throw new Error(`PDF generation failed for ${docGroup.name}: ${error}`)
              }
            })
        )) as any
      }
    }

    operations.push(operation)
    return operations
  }

  /**
   * Track document changes for reorganization
   */
  const trackDocumentChanges = (
    organizedDocuments: DocumentGroup[],
    originalDocumentState: DocumentGroup[],
    documents: Document[]
  ) => {
    const tracker = new Map<
      string,
      {
        originalUuid: string
        hasChanges: boolean
        changeType: 'modified' | 'deleted' | 'created' | 'unchanged'
        originalPageCount: number
        currentPageCount: number
        pageChanges: {
          added: string[]
          removed: string[]
          reordered: boolean
        }
      }
    >()

    // Extract all unique document UUIDs from original document state
    const originalDocumentUuids = new Set(
      originalDocumentState.flatMap((docGroup) => docGroup.pages.map((page) => page.documentUuid))
    )

    // Track changes for each original document UUID
    originalDocumentUuids.forEach((originalUuid) => {
      // Find the original document metadata from the documents array
      const originalDoc = documents.find((d) => d.uuid === originalUuid)
      const originalPageCount = originalDoc?.page_count || 1

      // Find original pages for this document
      const originalPages = originalDocumentState.flatMap((docGroup) =>
        docGroup.pages.filter((page) => page.documentUuid === originalUuid)
      )

      // Find current pages for this document in organized documents
      const currentPages = organizedDocuments.flatMap((docGroup) =>
        docGroup.pages.filter((page) => page.documentUuid === originalUuid)
      )

      if (currentPages.length === 0) {
        // Document was completely removed
        tracker.set(originalUuid, {
          originalUuid,
          hasChanges: true,
          changeType: 'deleted',
          originalPageCount: originalPages.length,
          currentPageCount: 0,
          pageChanges: { added: [], removed: [], reordered: false }
        })
        return
      }

      // Check if pages were reordered or moved between documents
      const hasPageCountChange = currentPages.length !== originalPages.length

      // Check if any pages from this document are now in different document groups
      const currentGroups = organizedDocuments.filter((docGroup) =>
        docGroup.pages.some((page) => page.documentUuid === originalUuid)
      )
      const originalGroups = originalDocumentState.filter((docGroup) =>
        docGroup.pages.some((page) => page.documentUuid === originalUuid)
      )

      // Check for page order changes within the same document group
      const hasPageOrderChanges =
        currentPages.some((currentPage, index) => {
          const originalPage = originalPages[index]
          return !originalPage || currentPage.uuid !== originalPage.uuid
        }) || currentPages.length !== originalPages.length

      // If the pages are now distributed across different groups than originally
      const hasStructuralChanges =
        currentGroups.length !== originalGroups.length ||
        hasPageCountChange ||
        hasPageOrderChanges ||
        // Check if any pages moved to a different document group
        currentPages.some((currentPage) => {
          const currentGroup = organizedDocuments.find((dg) =>
            dg.pages.some((p) => p.uuid === currentPage.uuid)
          )
          const originalGroup = originalDocumentState.find((dg) =>
            dg.pages.some((p) => p.uuid === currentPage.uuid)
          )
          return currentGroup?.name !== originalGroup?.name
        })

      if (hasStructuralChanges) {
        tracker.set(originalUuid, {
          originalUuid,
          hasChanges: true,
          changeType: 'modified',
          originalPageCount: originalPages.length,
          currentPageCount: currentPages.length,
          pageChanges: {
            added: [],
            removed: [],
            reordered: true
          }
        })
      } else {
        tracker.set(originalUuid, {
          originalUuid,
          hasChanges: false,
          changeType: 'unchanged',
          originalPageCount: originalPages.length,
          currentPageCount: currentPages.length,
          pageChanges: {
            added: [],
            removed: [],
            reordered: false
          }
        })
      }
    })

    return tracker
  }

  /**
   * Get change summary for reorganization
   */
  const getChangeSummary = (
    organizedDocuments: DocumentGroup[],
    originalDocumentState: DocumentGroup[]
  ) => {
    const currentDocCount = organizedDocuments.length
    const originalDocCount = originalDocumentState.length

    if (currentDocCount < originalDocCount) {
      return {
        type: 'merge',
        description: `Merged ${originalDocCount} documents into ${currentDocCount} documents`
      }
    } else if (currentDocCount > originalDocCount) {
      return {
        type: 'split',
        description: `Split ${originalDocCount} documents into ${currentDocCount} documents`
      }
    } else {
      // Same document count, check for page reordering or moves
      let hasPageMoves = false
      let hasPageReordering = false

      for (let i = 0; i < currentDocCount; i++) {
        const currentDoc = organizedDocuments[i]
        const originalDoc = originalDocumentState[i]

        if (!originalDoc) continue

        // Check for page reordering within the same document
        const currentPageUuids = currentDoc.pages.map((p) => p.uuid)
        const originalPageUuids = originalDoc.pages.map((p) => p.uuid)

        if (JSON.stringify(currentPageUuids) !== JSON.stringify(originalPageUuids)) {
          hasPageReordering = true
        }
      }

      // Check for pages moved between documents
      const allCurrentPageUuids = organizedDocuments.flatMap((doc) => doc.pages.map((p) => p.uuid))
      const allOriginalPageUuids = originalDocumentState.flatMap((doc) =>
        doc.pages.map((p) => p.uuid)
      )

      if (
        JSON.stringify(allCurrentPageUuids.sort()) !== JSON.stringify(allOriginalPageUuids.sort())
      ) {
        hasPageMoves = true
      }

      if (hasPageMoves || hasPageReordering) {
        return {
          type: 'reorganization',
          description: `Reorganized pages within ${currentDocCount} documents`
        }
      }
    }

    return { type: 'no_changes', description: 'No changes detected' }
  }

  /**
   * Generate AI title for a document
   */
  const generateAITitle = async (
    documentUuid: string,
    organizationState?: any,
    documentGroupName?: string
  ): Promise<string> => {
    try {
      const requestBody: any = {}

      if (organizationState && documentGroupName) {
        requestBody.current_organization_state = organizationState
        requestBody.document_group_name = documentGroupName
      }

      const response = await DocumentsService.documentsGenerateTitleCreate({
        uuid: documentUuid,
        requestBody: Object.keys(requestBody).length > 0 ? requestBody : undefined
      })

      if (!response.suggested_title) {
        throw new Error('No suggested title returned from AI')
      }

      return response.suggested_title
    } catch (error) {
      console.error('Error generating AI title:', error)
      throw error
    }
  }

  /**
   * Split document with AI
   */
  const splitDocumentWithAI = async (
    caseUuid: string,
    documentUuid: string,
    currentOrganizationState: any
  ) => {
    try {
      const response = await DocumentsService.documentsSplitWithAiCreate({
        requestBody: {
          case_uuid: caseUuid,
          document_uuid: documentUuid,
          current_organization_state: currentOrganizationState
        }
      })

      return response
    } catch (error) {
      console.error('Error splitting document with AI:', error)
      throw error
    }
  }

  /**
   * Update document metadata
   */
  const updateDocumentMetadata = async (
    documentUuid: string,
    updates: { title?: string; filename?: string }
  ) => {
    try {
      const response = await DocumentsService.documentsUpdate({
        uuid: documentUuid,
        requestBody: updates
      })

      return response
    } catch (error) {
      console.error('Error updating document metadata:', error)
      throw error
    }
  }

  /**
   * Process all pending name changes before saving documents
   */
  const processPendingNameChanges = async (
    pendingNameChanges: Map<string, string>
  ): Promise<{ successful: string[]; failed: { uuid: string; error: any }[] }> => {
    const successful: string[] = []
    const failed: { uuid: string; error: any }[] = []

    if (pendingNameChanges.size === 0) {
      return { successful, failed }
    }

    toast({
      title: t('info'),
      description: `Processing ${pendingNameChanges.size} name changes...`,
      variant: 'default'
    })

    // Process name changes in parallel for better performance
    const nameChangePromises = Array.from(pendingNameChanges.entries()).map(
      async ([documentUuid, newName]) => {
        try {
          await DocumentsService.documentsUpdate({
            uuid: documentUuid,
            requestBody: {
              filename: `${newName.trim()}.pdf`,
              title: newName.trim()
            }
          })
          successful.push(documentUuid)
        } catch (error) {
          console.error(`Failed to update name for document ${documentUuid}:`, error)
          failed.push({ uuid: documentUuid, error })
        }
      }
    )

    await Promise.all(nameChangePromises)

    if (failed.length > 0) {
      toast({
        title: t('warning'),
        description: `${failed.length} of ${pendingNameChanges.size} name changes failed and will be retried`,
        variant: 'destructive'
      })
    } else if (successful.length > 0) {
      toast({
        title: t('success'),
        description: `Successfully processed ${successful.length} name changes`,
        variant: 'default'
      })
    }

    return { successful, failed }
  }

  return {
    // Save operations
    saveDocumentReorganization,
    generateReorganizationOperations,

    // Change tracking
    trackDocumentChanges,
    getChangeSummary,

    // AI operations
    generateAITitle,
    splitDocumentWithAI,

    // Document operations
    updateDocumentMetadata,
    processPendingNameChanges
  }
}
